import React, { useState, useEffect } from "react";
import "./UpdateTestimonial.scss";
import { useFirestore } from "../firebase-files/hooks/useFirestore";
import Rating from "@mui/material/Rating";
import StarIcon from "@mui/icons-material/Star";
import ReactSwitch from "react-switch";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { projectStorage } from "../firebase-config/config";

const UpdateTestimonial = ({ testimonial, onClose }) => {
  const { user } = useAuthContext();
  const { updateDocument } = useFirestore("testimonials");

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    description: "",
    rating: 0,
    status: "inactive",
  });

  // For images
  const [mainImage, setMainImage] = useState(null);
  const [overlayImage, setOverlayImage] = useState(null);

  // Track which image input is active
  const [activeImageInput, setActiveImageInput] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (testimonial) {
      setFormData({
        name: testimonial.name || "",
        role: testimonial.role || "",
        description: testimonial.description || "",
        rating: testimonial.rating || 0,
        status: testimonial.status || "inactive",
      });
    }
  }, [testimonial]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleToggleChange = () => {
    setFormData((prev) => ({
      ...prev,
      status: prev.status === "active" ? "inactive" : "active",
    }));
  };

  const uploadImageWithDocId = async (file, docId, type) => {
    if (!file || !docId) return "";
    try {
      const storageRef = projectStorage.ref(
        `testimonialImages/${docId}/${type}.jpg`
      );
      await storageRef.put(file);
      return await storageRef.getDownloadURL();
    } catch (error) {
      console.error("Image upload failed:", error);
      return "";
    }
  };

  const handleUpdate = async () => {
    if (!testimonial?.id) return;
    setUploading(true);

    try {
      const updatedData = { ...formData };

      if (mainImage) {
        const mainUrl = await uploadImageWithDocId(
          mainImage,
          testimonial.id,
          "main"
        );
        if (mainUrl) updatedData.mainImg = mainUrl;
      }

      if (overlayImage) {
        const overlayUrl = await uploadImageWithDocId(
          overlayImage,
          testimonial.id,
          "overlay"
        );
        if (overlayUrl) updatedData.overlayImg = overlayUrl;
      }

      await updateDocument(testimonial.id, updatedData);
      onClose();
    } catch (error) {
      console.error("Update failed", error);
      alert("Failed to update testimonial");
    }

    setUploading(false);
  };

  // Handle combined file input change
  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    if (activeImageInput === "main") {
      setMainImage(file);
    } else if (activeImageInput === "overlay") {
      setOverlayImage(file);
    }
    setActiveImageInput(null); // reset after selection
  };

  return (
    <div className="update-testimonial-modal-overlay-parent">
      <div className="update-modal-content">
        <h2>Update Testimonial</h2>

        <input
          type="text"
          name="name"
          placeholder="Customer Name"
          value={formData.name}
          onChange={handleChange}
        />

        <input
          type="text"
          name="role"
          placeholder="Role"
          value={formData.role}
          onChange={handleChange}
        />

        <textarea
          name="description"
          placeholder="Description"
          value={formData.description}
          onChange={handleChange}
        />

        <div className="star-rating-box">
          <Rating
            name="rating"
            value={formData.rating}
            precision={0.5}
            onChange={(event, newValue) =>
              setFormData({ ...formData, rating: newValue })
            }
            emptyIcon={
              <StarIcon style={{ opacity: 0.45 }} fontSize="inherit" />
            }
          />
          <p className="rating-text">Rating: {formData.rating || "None"}</p>
        </div>

      
          <div className="toggle-wrapper">
            <label>Status:</label>
            <ReactSwitch
              checked={formData.status === "active"}
              onChange={handleToggleChange}
              onColor="#149AA2"
              offColor="#F05B83"
              className="products-status-switch"
            />
            <span className="status-label">{formData.status}</span>
          </div>

        {/* Hidden combined file input */}
        <input
          type="file"
          id="image-file-input"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Buttons to trigger file select for main or overlay image */}
        <div className="image-upload-buttons">
          <button
            className="image-upload-button"
            type="button"
            onClick={() => {
              setActiveImageInput("main");
              document.getElementById("image-file-input").click();
            }}
          >
            Update Image
          </button>
          {mainImage && (
            <p className="selected-file">Selected main: {mainImage.name}</p>
          )}

          <button
            className="image-upload-button"
            type="button"
            onClick={() => {
              setActiveImageInput("overlay");
              document.getElementById("image-file-input").click();
            }}
          >
            Update User Image
          </button>
          {overlayImage && (
            <p className="selected-file">Selected user: {overlayImage.name}</p>
          )}
        </div>

        <div className="button-group">
          <button
            className="add-btn"
            onClick={handleUpdate}
            disabled={uploading}
          >
            {uploading ? "Updating..." : "Update"}
          </button>
          <button className="cancel-btn" onClick={onClose} disabled={uploading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateTestimonial;
